import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { lookupStatic } from '@/lib/i18n/staticDictionary';

/**
 * Runtime UI translator. When the active language is English, walks the DOM,
 * finds text nodes containing Cyrillic (Mongolian) characters, looks each one
 * up in the static `MN_TO_EN` dictionary, and swaps the text in place.
 * Untranslated strings are left untouched (graceful fallback). No AI / no
 * network calls.
 */

const CYRILLIC = /[\u0400-\u04FF]/;
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT',
  'SVG', 'PATH', 'CANVAS', 'IFRAME',
]);

function collectCyrillicNodes(root: Node, out: Text[]): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const t = node.nodeValue;
      if (!t || !CYRILLIC.test(t) || !t.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (parent.closest('[data-no-translate]')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) out.push(n as Text);
}

export function AutoTranslator() {
  const { language } = useLanguage();

  useEffect(() => {
    if (language !== 'en') {
      document.querySelectorAll<HTMLElement>('[data-orig-text]').forEach((el) => {
        const orig = el.getAttribute('data-orig-text');
        if (orig != null) {
          if (el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE) {
            el.firstChild.nodeValue = orig;
          }
          el.removeAttribute('data-orig-text');
        }
      });
      return;
    }

    let scheduled = 0;

    function applyTranslation(node: Text, orig: string, tr: string) {
      if ((node.nodeValue ?? '').trim() !== orig) return;
      const parent = node.parentElement;
      if (parent && !parent.hasAttribute('data-orig-text')) {
        parent.setAttribute('data-orig-text', node.nodeValue ?? '');
      }
      const raw = node.nodeValue ?? '';
      const leading = raw.match(/^\s*/)?.[0] ?? '';
      const trailing = raw.match(/\s*$/)?.[0] ?? '';
      node.nodeValue = leading + tr + trailing;
    }

    function applyAll(nodes: Text[]) {
      for (const node of nodes) {
        const orig = (node.nodeValue ?? '').trim();
        if (!orig) continue;
        const tr = lookupStatic(orig);
        if (tr) applyTranslation(node, orig, tr);
      }
    }

    function scan() {
      window.clearTimeout(scheduled);
      scheduled = window.setTimeout(() => {
        const nodes: Text[] = [];
        collectCyrillicNodes(document.body, nodes);
        applyAll(nodes);
      }, 100);
    }

    scan();
    const obs = new MutationObserver(() => scan());
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      obs.disconnect();
      window.clearTimeout(scheduled);
    };
  }, [language]);

  return null;
}

export default AutoTranslator;